import React from 'react';

import { computed, observable, action, reaction, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import { Checkbox, SearchBox } from '@fluentui/react';
import { mergeStyleSets, mergeStyles, FontSizes, FontWeights } from '@fluentui/react';

import DefaultButtonEx from '@v2/components/base/DefaultButtonEx'
import { ModalPage } from 'src/components/base/Page';

import SearchableComboBox from 'src/components/base/SearchableComboBox';
import Separator from 'src/components/base/Separator';

import { runShortAction } from 'src/services/utils';
import { includesParttern, omit, asArray } from 'shared/utils/utils'

import { FieldSelector } from './common';
import { Constants } from './Constants';
import { OwnerChangeOptions } from './Settings';
import * as Validation from './validation';
import Manager from './Manager';


const classNames = mergeStyleSets({
  root: {
    margin: '12px 12px 120px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',

    selectors: {
      '& > div': {
        marginTop: '10px'
      }
    },
  },

  dropdown: {
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'center',
    justifyContent: 'stretch',
    selectors: {
      '& > :first-child': {
        alignSelf: 'flex-start',
        marginTop: '7px',
      },
      '& > :last-child': {
        marginLeft: '8px',
        width: '100%',
      }
    },
  },

  lookup: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '8px',
    marginLeft: '16px',
    selectors: {
      '& > span': {
        fontWeight: FontWeights.semibold,
      },
    }
  },

  mapping: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateAreas :
    `
    "mappingHeader mappingHeader"
    "mappingCheckbox mappingField"
    ". lookupHeader"
    ". lookupField"
    `,
    gridTemplateColumns: 'min-content 1fr',
    gridTemplateRows: 'repeat(auto-fill, min-content)',
    gridColumnGap: '8px',
    gridRowGap: '5px',
    selectors: {
      '& > span': {
        fontWeight: FontWeights.semibold,
      },

    },
  },
  mappingFieldheader: {
    gridArea: 'mappingHeader'
  },
  mappingCheckbox: {
    gridArea: 'mappingCheckbox'
  },
  mappingField: {
    gridArea: 'mappingField'
  },
  mappingLookupHeader: {
    gridArea: 'lookupHeader'
  },
  mappingLookup: {
    gridArea: 'lookupField'
  },

  errorMessage: {
    display: 'block',
    marginTop: '10px',
    color: '#a4262c',
    textAlign: 'center',
    width: '100%',
  },

  buttonsBlock: {
    display: 'flex',
    flexFlow: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    margin: '8px 0 0 -16px',

    '& .owner-button>button': {
      padding: '4px 0'
    },

    '& .constant-button': {
      height: '100%',
    },

    '& .constant-button>button': {
      height: '100%',
    },

    selectors: {
      '& > *': {
        flexGrow: 1,
        flexBasis: 0,
        marginLeft: '16px',
      },
    },
  },

});


// const mmType = MappingManager.Type.Mapping;

@observer
export default class Mapping extends React.Component {
  @observable mappedHeaders = [];
  @observable upsertState = {
    columnIndex: 0,
    error: ''
  };

  @observable validationResult = new Validation.ValidationResult();
  @observable excelHeadersFilter = '';
  @observable showConstantsPage = false;
  @observable showSettingsPage = false;


  @computed get mappingManager() {
    return this.props.mappingManager;
  }

  @computed get isDisabled() {
    return this.props.isDisabled;
  }

  @computed get objectFields() {
    return this.mappingManager.objectFields;
  }

  @computed get pageCommands() {
    return this.isDisabled
      ? []
      : [
        { key: 'reset', iconProps: { iconName: 'Clear' }, onClick: this.handleResetMapping },
        { key: 'apply',
          iconProps: { iconName: !this.errorMessage ? 'Accept' : 'UserWarning' },
          ...(this.validationResult.hasAnyError ? { callout: this.errorMessage } : { onClick: this.handleApplyMapping })
        },
      ];
  }

  @computed get areAllHeadersSelected() {
    return this.filteredMappedHeaders.every(x => x.isUsed);
  }

  @computed get filteredMappedHeaders() {
    return this.excelHeadersFilter
      ? this.mappedHeaders.filter(x => includesParttern(x.excelHeader.name, this.excelHeadersFilter))
      : [...this.mappedHeaders];
  }

  @computed get columnNameOptions() {
    return this.mappingManager.excelHeaderOptions.map((text, index) => ({ key: `${index}`, text, columnIndex: index }));
  }

  @computed get externalIdFieldName() {
    const { columnIndex } = this.upsertState;
    const { fieldData } = columnIndex !== -1
      ? this.mappedHeaders[columnIndex]
      : {};
    return fieldData?.name ?? '';
  }

  @computed get errorMessage() {
    const { mapping, general } = this.validationResult;
    return mapping.length
      ? 'Some mappings have problems'
      : general.length
        ? general[0].message
        : '';
  }

  @computed get showOwnerChangeSettings() {
    return this.mappingManager?.isInsert;
  }

  constructor(props) {
    super(props);
    makeObservable(this);

    const r1 = reaction(
      () => ({ pageCommands: this.pageCommands, mappedHeaders: this.mappedHeaders }),
      () => {
        this.props.pageControl.setHeaderCommands(this.pageCommands);
      }
    );

    this.cleanReactions = () => { r1(); };
  }

  async componentDidMount() {
    await runShortAction(async () => {
      // this.savedFieldsOrder = this.mappingManager.saveUsedFields(mmType);
      await this.mappingManager.loadObjectFields();
      this.initializeMapping();
    });
  }

  componentWillUnmount() {
    this.cleanReactions();
  }

  async updateLookupOptions(fieldData) {
    if (fieldData.isLookup) {
      const onlyDefault = !!fieldData.lookupOptions;
      if (!onlyDefault) {
        const options = await this.mappingManager.getLookupOptions(fieldData);
        fieldData.lookupOptions = options;
      }
      const defaultOption = this.mappingManager.getDefaultLookupOption(fieldData);
      return defaultOption;
    }
  }

  @action async loadLookupOptions() {
    await runShortAction(async () => {
      this.mappedHeaders = await Promise.all(this.mappedHeaders.map(async (mappedHeader) => {
        const { fieldData } = mappedHeader;
        if (mappedHeader.fieldData) {
          const defaultOption = await this.updateLookupOptions(fieldData);
          if (!mappedHeader.lookupObject) {
            mappedHeader.lookupObject = defaultOption;
          }
        }
        return mappedHeader;
      }));
    });
  }

 @action async initializeMapping() {
    const res = this.mappingManager.loadMapping(!this.isDisabled);
    const { mappedHeaders, externalIdFieldName } = res;


    // TODO: investigate, why this.setDirty() called from within MappingManager
    // doesn't set dirty flag, but
    res.isDirty && this.setDirty();
    this.mappedHeaders = mappedHeaders.map(x => ({ fieldError: null, isDisabled: false, ...x }) );

    this.upsertState.columnIndex = externalIdFieldName
      ? this.mappedHeaders.findIndex(({ fieldData }) => fieldData?.name === externalIdFieldName)
      : -1;
    await this.loadLookupOptions();
    this.checkMapping();
  }

  @action checkMapping() {
    const { mappingManager } = this;
    const ctx = { mappedHeaders: this.mappedHeaders, externalIdFieldName: this.externalIdFieldName };
    const result = this.mappingManager.validate(ctx, Validation.Source.All);
    this.validationResult = result;

    this.mappedHeaders.forEach(header => {
      const { isDisabled, errors } = result.getFieldStatus(header);
      header.isDisabled = isDisabled;
      header.fieldError = errors.length ? errors[0] : null;
    });

    if (mappingManager.isUpsert) {
      this.upsertState.error = result.mapping.find(x => x.type === Validation.ErrorType.InvalidUpsert)?.message;
    }
  }


  render() {
    return (
      <div className={classNames.root}>

        <div className={classNames.buttonsBlock}>

          <DefaultButtonEx
            text={`Constants (${this.mappingManager.status.constants.totalCount})`}
            onClick={this.handleOpenConstantsPage}
            primary={true}
            error={this.validationResult.constants.length && 'Verify constants'}
            className='constant-button'
          />

          <DefaultButtonEx
            text={'Owner Change Options'}
            onClick={this.handleOpenSettingsPage}
            tooltip={`Allows to set actions that can be performed when a record's owner is changed`}
            disabled={!this.mappingManager.showOwnerChangeSettings}
            primary={true}
            className='owner-button'
          />

        </div>

        <Separator />

        <div className={classNames.dropdown}>
          <Checkbox checked={this.areAllHeadersSelected} onChange={(_, checked) => this.handleSelectAllHeaders(checked)}/>
          <SearchBox
            placeholder='Filter columns by name'
            onChange={event => this.handleFilterChange(event)}
            onClear={() => this.handleFilterChange(null)}
            iconProps={{ iconName: 'Filter' }}
          />
        </div>

        <Separator error={this.errorMessage} />

        {this.filteredMappedHeaders.map(mappedHeader => {
            return (
              <MappedHeader
                key={mappedHeader.id}
                options={this.mappingManager.objectFieldOptions}
                mappedHeader={mappedHeader}
                disabled={this.isDisabled}
                onUsageChange={this.handleFieldUsage}
                onFieldChange={this.handleFieldChange}
                onLookupObjectChange={this.handleLookupObjectChange}
              />
            )
          })
        }

        { this.mappingManager.isUpsert && !!this.mappedHeaders.length &&
        <>
          <Separator />

          <div>
            {/* <p className='ms-fontSize-m ms-fontWeight-semibold'></p> */}
            <SearchableComboBox
              label={'Select the column with external IDs'}
              placeholder={`Select column`}
              selectedKey={`${this.upsertState.columnIndex}`}
              onChange={(_, item) => this.handleSelectExternalIdColumnName(item)}
              options={this.columnNameOptions}
              errorMessage={this.upsertState.error}
              disabled={this.isDisabled}
            />
          </div>
        </>
        }

        { this.constantsPage() }
        { this.settingsPage() }
      </div>
    );
  }

  @action handleLookupObjectChange = (id, fieldData) => {
    this.mappedHeaders = this.mappedHeaders.map(item => {
      if (item.id !== id) {
        return item;
      }
      return this.updateFieldUsage(item, {
        ...item,
        lookupObject: {
          name: fieldData.rawField.name,
          objectName: fieldData.objectName
        }
      });
    });
    this.checkMapping();
    this.setDirty();
  }

  @action handleFieldChange = (id, fieldData) => {
    runShortAction(async () => {
      this.mappedHeaders = await Promise.all(this.mappedHeaders.map(async (item) => {
        if (item.id !== id) {
          return item;
        }
        if (fieldData) {
          const defaultOption = await this.updateLookupOptions(fieldData);
          item.lookupObject = defaultOption;
        }
        else {
          item.lookupObject = null;
        }

        return this.updateFieldUsage(item, { ...item, fieldData });
      }));
      this.checkMapping();
    })
    this.setDirty();
  }

  @action handleFieldUsage = (idOrIds, isUsed) => {
    const ids = asArray(idOrIds);
    this.mappedHeaders = this.mappedHeaders.map(item => ids.includes(item.id)
        ? this.updateFieldUsage(item, { ...item, isUsed })
        : item);
    // console.log('handleFieldUsage: ', ids, isUsed, this.mappedHeaders);
    // this.updateChangeOrder(ids);
    this.checkMapping();
    this.setDirty();
  }

  @action updateFieldUsage(oldItem, newItem) {
    this.mappingManager.removeFromUsedFields(oldItem);
    this.mappingManager.addToUsedFields(newItem);
    return newItem;
  }

  @action handleSelectExternalIdColumnName(item) {
    const { columnIndex } = item;
    this.upsertState.columnIndex = columnIndex;
    this.checkMapping();
    this.setDirty();
  }

  @action handleFilterChange(event) {
    this.excelHeadersFilter = event?.target?.value ?? '';
  }

  @action handleSelectAllHeaders(isUsed) {
    this.handleFieldUsage(this.filteredMappedHeaders.map(x => x.id), isUsed);
  }

  @action setDirty = (isDirty = true) => {
    this.props.pageControl.setDirtyActions(isDirty
      ? {
        text: 'Mapping changed',
        onSave: async () => this.notifyMappingChanged(),
        onForget: async () => this.handleForgetChanges(),
        disableSave: !!this.validationResult.hasAnyError,
      }
      : null);
  }

  @action handleResetMapping = async () => {
    this.handleForgetChanges();
    this.initializeMapping();
  }

  @action handleForgetChanges = () => {
    this.mappingManager.forgetChanges(Validation.Source.Mapping);
  }

  @action handleApplyMapping = () => {
    this.notifyMappingChanged();
  }

  @action notifyMappingChanged = () => {
    this.setDirty(false);

    const { columnIndex } = this.upsertState;
    const { fieldData } = columnIndex !== -1
      ? this.mappedHeaders[columnIndex]
      : {};
    const externalIdFieldName = fieldData?.name ?? '';
    this.mappingManager.saveMappedHeaders({
      mappedHeaders: this.mappedHeaders.map(x => omit(x, ['fieldError', 'isDisabled'])),
      externalIdFieldName,
    });
    this.props.onChange();
  }


  handleOpenConstantsPage = () => {
    this.showConstantsPage = true;
  }

  @action handleApplyConstants = () => {
    this.showConstantsPage = false;
    this.checkMapping();
  }

  constantsPage = () => {
    return <ModalPage title="Constants"
      isOpen={this.showConstantsPage}
      onClose={this.handleApplyConstants}
      component={this.constantsComponent}
    />
  }

  constantsComponent = (props) => {
    return <Constants
      mappingManager={this.mappingManager}
      isDisabled={this.isDisabled}
      onChange={this.handleApplyConstants}
      {...props}
    />
  }


  handleOpenSettingsPage = () => {
    this.showSettingsPage = true;
  }

  @action handleApplySettings = (settings) => {
    this.showSettingsPage = false;
  }

  settingsPage = () => {
    return <ModalPage title="Owner Change Options"
      isOpen={this.showSettingsPage}
      onClose={this.handleApplySettings}
      component={this.settingsComponent}
    />
  }

  settingsComponent = (props) => {
    return <OwnerChangeOptions
      mappingManager={this.mappingManager}
      onChange={this.handleApplySettings}
      {...props}
    />
  }
};


function MappedHeader({ mappedHeader, disabled, options, onUsageChange, onFieldChange, onLookupObjectChange }) {
  const { isUsed, id, fieldData, fieldError, isDisabled: disabledColumn, excelHeader, lookupObject } = mappedHeader;
  const isDisabled = disabled || disabledColumn;

  return (
    <div className={classNames.mapping}>
      <span className={classNames.mappingFieldheader}>{`Column '${excelHeader.name}'${disabledColumn ? ' (ignored)' : ''}`}</span>
        <Checkbox
          className={classNames.mappingCheckbox}
          checked={isUsed}
          disabled={isDisabled}
          onChange={(event, checked) => onUsageChange(id, checked)}
        />
        <div className={classNames.mappingField}>
        <FieldSelector
          placeholder={`Select field to map on '${excelHeader.name}' column`}
          selectedKey={fieldData?.name}
          onChange={(item) => onFieldChange(id, item.fieldData)}
          options={options}
          errorMessage={fieldError?.message}
          disabled={isDisabled}
        />
        </div>

      {fieldData?.isLookup && !!fieldData.lookupOptions?.length &&
        <>
          <span className={classNames.mappingLookupHeader}>{`If not id match by:`}</span>
          <div className={classNames.mappingLookup}>
          <FieldSelector
            disabled={isDisabled}
            placeholder={'Select lookup'}
            selectedKey={lookupObject && Manager.getLookupFieldKey(lookupObject)}
            onChange={(item) => onLookupObjectChange(id, item.fieldData)}
            options={fieldData.lookupOptions}
          />
          </div>
        </>
      }
    </div>
  );
}
